/**
 * vehicleEquipmentPresets.ts
 *
 * Presets profil véhicule complet pour accélérer la saisie
 * et éviter les configurations incohérentes.
 *
 * Chaque preset représente un véhicule réel typique du transport routier EU
 * avec configuration complète: dimensions, essieux, sellette, contraintes.
 */

import type { VehicleAxleConfiguration } from '../types/vehiclePhysics'
import { vehicleInteriorVolume } from './vehicleFormContract'

// ─── Structure d'un preset profil véhicule complet ─────────────────────────────

export interface VehicleEquipmentPreset {
  /** Identifiant unique du preset */
  id: string
  /** Label affiché dans l'UI */
  label: string
  /** Description détaillée */
  description: string
  /** Icône ou catégorie pour le regroupement UI */
  category: 'van' | 'porteur' | 'tracteur' | 'semi' | 'remorque' | 'conteneur'
  /** Type véhicule standard */
  vehicleType: 'TRUCK' | 'RIGID_TRUCK' | 'VAN' | 'TRAILER' | 'SEMI_TRAILER' | 'CONTAINER' | 'OTHER'
  /** Dimensions véhicule (mm) */
  dimensions?: {
    length: number
    width: number
    height: number
  }
  /** Capacités */
  capacity?: {
    volume?: number // m³, derived from the configured geometry; absent for tractors
    /** @deprecated v1 : ne pas utiliser pour une masse brute ; voir mass. */
    weight?: number // kg
  }
  /** Contrat de masses versionné du catalogue. Les valeurs restent indicatives. */
  mass?: {
    version: 'vehicle_preset_mass.v1'
    source: 'indicative_catalog'
    /** PTAC ou masse brute maximale du conteneur, en kg. */
    individualGrossWeight?: number
    /** PTRA, défini seulement si le catalogue connaît la plaque du tracteur. */
    combinationGrossWeight?: number
  }
  /** Configuration essieux */
  axleConfiguration: VehicleAxleConfiguration
  /** Configuration sellette (si applicable) */
  fifthWheel?: {
    position: number // mm
    type: 'standard' | 'low' | 'high'
    maxLoad: number // kg
  }
  /** Contraintes d'optimisation */
  constraints?: {
    maxWeight?: number // kg
    maxStackHeight?: number // mm
    maxFloorPressure?: number // kg/m²
    pressureCellTargetSize?: number // mm
  }
  /** Centre de gravité cible */
  targetCG?: {
    x: number // 0-1 (longitudinal)
    y: number // 0-1 (latéral)
  }
  /** Poids à vide total du véhicule (kg) - somme des poids à vide des essieux */
  emptyWeight?: number
}

// ─── Catalogue des presets ────────────────────────────────────────────────────

const PRESET_DEFINITIONS: Record<string, VehicleEquipmentPreset> = {

  // ── VAN / Fourgonnette ──────────────────────────────────────────────────────
  van_standard: {
    id: 'van_standard',
    label: 'Fourgonnette standard',
    description: 'Véhicule utilitaire léger 3.5t, 2 essieux',
    category: 'van',
    vehicleType: 'VAN',
    dimensions: { length: 6000, width: 2100, height: 2200 },
    capacity: { weight: 3500 },
    axleConfiguration: {
      physicsType: 'van',
      trackWidth: 1800,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front', position: 1000, type: 'steering', maxWeight: 1800, emptyWeight: 900 },
        { id: 'rear',  position: 3200, type: 'drive',    maxWeight: 1950, emptyWeight: 700 },
      ],
    },
    constraints: {
      maxWeight: 3500,
      maxStackHeight: 2000,
      maxFloorPressure: 5000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 1600,
  },

  // ── Porteur 2 essieux ───────────────────────────────────────────────────────
  rigid_truck_2_axles_standard: {
    id: 'rigid_truck_2_axles_standard',
    label: 'Porteur 2 essieux (12t) - Standard',
    description: 'Porteur rigide 12 tonnes, 2 essieux EU standard - Empattement 4200mm',
    category: 'porteur',
    vehicleType: 'RIGID_TRUCK',
    dimensions: { length: 8000, width: 2500, height: 3000 },
    capacity: { weight: 12000 },
    axleConfiguration: {
      physicsType: 'rigid_truck',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front', position: 1200, type: 'steering', maxWeight: 7500,  emptyWeight: 3200 },
        { id: 'rear',  position: 5400, type: 'drive',    maxWeight: 11500, emptyWeight: 2500 },
      ],
    },
    constraints: {
      maxWeight: 12000,
      maxStackHeight: 2500,
      maxFloorPressure: 8000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 5700,
  },

  rigid_truck_2_axles_short: {
    id: 'rigid_truck_2_axles_short',
    label: 'Porteur 2 essieux (12t) - Court',
    description: 'Porteur rigide 12 tonnes, 2 essieux EU court - Empattement 3900mm',
    category: 'porteur',
    vehicleType: 'RIGID_TRUCK',
    dimensions: { length: 7500, width: 2500, height: 3000 },
    capacity: { weight: 12000 },
    axleConfiguration: {
      physicsType: 'rigid_truck',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front', position: 1200, type: 'steering', maxWeight: 7500,  emptyWeight: 3200 },
        { id: 'rear',  position: 5100, type: 'drive',    maxWeight: 11500, emptyWeight: 2500 },
      ],
    },
    constraints: {
      maxWeight: 12000,
      maxStackHeight: 2500,
      maxFloorPressure: 8000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 5700,
  },

  rigid_truck_2_axles_long: {
    id: 'rigid_truck_2_axles_long',
    label: 'Porteur 2 essieux (12t) - Long',
    description: 'Porteur rigide 12 tonnes, 2 essieux EU long - Empattement 4500mm',
    category: 'porteur',
    vehicleType: 'RIGID_TRUCK',
    dimensions: { length: 8500, width: 2500, height: 3000 },
    capacity: { weight: 12000 },
    axleConfiguration: {
      physicsType: 'rigid_truck',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front', position: 1200, type: 'steering', maxWeight: 7500,  emptyWeight: 3200 },
        { id: 'rear',  position: 5700, type: 'drive',    maxWeight: 11500, emptyWeight: 2500 },
      ],
    },
    constraints: {
      maxWeight: 12000,
      maxStackHeight: 2500,
      maxFloorPressure: 8000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 5700,
  },

  rigid_truck_2_axles_extra_long: {
    id: 'rigid_truck_2_axles_extra_long',
    label: 'Porteur 2 essieux (12t) - Extra long',
    description: 'Porteur rigide 12 tonnes, 2 essieux EU extra long - Empattement 5200mm',
    category: 'porteur',
    vehicleType: 'RIGID_TRUCK',
    dimensions: { length: 9500, width: 2500, height: 3000 },
    capacity: { weight: 12000 },
    axleConfiguration: {
      physicsType: 'rigid_truck',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front', position: 1200, type: 'steering', maxWeight: 7500,  emptyWeight: 3200 },
        { id: 'rear',  position: 6400, type: 'drive',    maxWeight: 11500, emptyWeight: 2500 },
      ],
    },
    constraints: {
      maxWeight: 12000,
      maxStackHeight: 2500,
      maxFloorPressure: 8000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 5700,
  },

  rigid_truck_2_axles_18t_short: {
    id: 'rigid_truck_2_axles_18t_short',
    label: 'Porteur 2 essieux (18t) - Court',
    description: 'Porteur rigide 18 tonnes, 2 essieux EU court - Empattement 4300mm',
    category: 'porteur',
    vehicleType: 'RIGID_TRUCK',
    dimensions: { length: 7800, width: 2500, height: 3000 },
    capacity: { weight: 18000 },
    axleConfiguration: {
      physicsType: 'rigid_truck',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front', position: 1200, type: 'steering', maxWeight: 9000,  emptyWeight: 3000 },
        { id: 'rear',  position: 5500, type: 'drive',    maxWeight: 9000,  emptyWeight: 2500 },
      ],
    },
    constraints: {
      maxWeight: 18000,
      maxStackHeight: 2500,
      maxFloorPressure: 8000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 5500,
  },

  rigid_truck_2_axles_18t_standard: {
    id: 'rigid_truck_2_axles_18t_standard',
    label: 'Porteur 2 essieux (18t) - Standard',
    description: 'Porteur rigide 18 tonnes, 2 essieux EU standard - Empattement 4600mm',
    category: 'porteur',
    vehicleType: 'RIGID_TRUCK',
    dimensions: { length: 8200, width: 2500, height: 3000 },
    capacity: { weight: 18000 },
    axleConfiguration: {
      physicsType: 'rigid_truck',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front', position: 1200, type: 'steering', maxWeight: 9000,  emptyWeight: 3000 },
        { id: 'rear',  position: 5800, type: 'drive',    maxWeight: 9000,  emptyWeight: 2500 },
      ],
    },
    constraints: {
      maxWeight: 18000,
      maxStackHeight: 2500,
      maxFloorPressure: 8000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 5500,
  },

  rigid_truck_2_axles_18t_long: {
    id: 'rigid_truck_2_axles_18t_long',
    label: 'Porteur 2 essieux (18t) - Long',
    description: 'Porteur rigide 18 tonnes, 2 essieux EU long - Empattement 5000mm',
    category: 'porteur',
    vehicleType: 'RIGID_TRUCK',
    dimensions: { length: 8800, width: 2500, height: 3000 },
    capacity: { weight: 18000 },
    axleConfiguration: {
      physicsType: 'rigid_truck',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front', position: 1200, type: 'steering', maxWeight: 9000,  emptyWeight: 3000 },
        { id: 'rear',  position: 6200, type: 'drive',    maxWeight: 9000,  emptyWeight: 2500 },
      ],
    },
    constraints: {
      maxWeight: 18000,
      maxStackHeight: 2500,
      maxFloorPressure: 8000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 5500,
  },

  // ── Tracteur 4x2 ─────────────────────────────────────────────────────────────
  tractor_4x2: {
    id: 'tractor_4x2',
    label: 'Tracteur 4x2',
    description: 'Tracteur 4x2 standard EU',
    category: 'tracteur',
    vehicleType: 'TRUCK',
    dimensions: { length: 5900, width: 2500, height: 3500 },
    capacity: { weight: 0 },
    axleConfiguration: {
      physicsType: 'truck',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      // Empattement 3 600 mm, usuel pour un tracteur routier 4x2.
      fifthWheelPosition: 4750, // Position de la sellette (mm, relatif à l'avant du tracteur)
      fifthWheelHeight: 1150,
      fifthWheelMaxLoad: 12000, // Charge max sellette (kg)
      axles: [
        { id: 'front', position: 1500, type: 'steering', maxWeight: 7500,  emptyWeight: 3500 },
        { id: 'drive', position: 5100, type: 'drive',    maxWeight: 11500, emptyWeight: 3000 },
      ],
    },
    constraints: {
      maxWeight: 19000,
      maxStackHeight: 0,
      maxFloorPressure: 0,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 6500,
  },

  // ── Tracteur 6x2 ─────────────────────────────────────────────────────────────
  tractor_6x2: {
    id: 'tractor_6x2',
    label: 'Tracteur 6x2',
    description: 'Tracteur 6x2 standard EU',
    category: 'tracteur',
    vehicleType: 'TRUCK',
    dimensions: { length: 6700, width: 2500, height: 3500 },
    capacity: { weight: 0 },
    axleConfiguration: {
      physicsType: 'truck',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      // Empattement 3 500 mm et entraxe moteur/tag de 1 350 mm, configuration 6x2 standard.
      fifthWheelPosition: 4700, // Position de la sellette (mm, relatif à l'avant du tracteur)
      fifthWheelHeight: 1150,
      fifthWheelMaxLoad: 12000, // Charge max sellette (kg)
      axles: [
        { id: 'front', position: 1500, type: 'steering', maxWeight: 7500,  emptyWeight: 3500 },
        { id: 'drive', position: 5000, type: 'drive',    maxWeight: 11500, emptyWeight: 3000 },
        { id: 'tag',   position: 6350, type: 'tag',      maxWeight: 8000,  emptyWeight: 1500 },
      ],
      axleGroups: [
        {
          id: 'tandem_rear',
          type: 'tandem',
          axleIds: ['drive', 'tag'],
          maxWeight: 19000,
          autoDetected: false,
        },
      ],
    },
    constraints: {
      maxWeight: 27000,
      maxStackHeight: 0,
      maxFloorPressure: 0,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 8000,
  },

  // ── Porteur 3 essieux ───────────────────────────────────────────────────────
  rigid_truck_3_axles_standard: {
    id: 'rigid_truck_3_axles_standard',
    label: 'Porteur 3 essieux (26t) - Standard',
    description: 'Porteur rigide 26 tonnes, 3 essieux, tandem arrière EU standard',
    category: 'porteur',
    vehicleType: 'RIGID_TRUCK',
    dimensions: { length: 10000, width: 2550, height: 3200 },
    capacity: { weight: 26000 },
    axleConfiguration: {
      physicsType: 'rigid_truck',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front',    position: 1300, type: 'steering', maxWeight: 7500,  emptyWeight: 3500 },
        { id: 'drive',    position: 5200, type: 'drive',    maxWeight: 11500, emptyWeight: 2800 },
        { id: 'tag',      position: 6400, type: 'tag',      maxWeight: 11500, emptyWeight: 1500 },
      ],
      axleGroups: [
        {
          id: 'tandem_rear',
          type: 'tandem',
          axleIds: ['drive', 'tag'],
          maxWeight: 19000,
          autoDetected: false,
        },
      ],
    },
    constraints: {
      maxWeight: 26000,
      maxStackHeight: 2600,
      maxFloorPressure: 10000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 7800,
  },

  rigid_truck_3_axles_long: {
    id: 'rigid_truck_3_axles_long',
    label: 'Porteur 3 essieux (26t) - Long',
    description: 'Porteur rigide 26 tonnes, 3 essieux, tandem arrière EU long',
    category: 'porteur',
    vehicleType: 'RIGID_TRUCK',
    dimensions: { length: 10500, width: 2550, height: 3200 },
    capacity: { weight: 26000 },
    axleConfiguration: {
      physicsType: 'rigid_truck',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'front',    position: 1300, type: 'steering', maxWeight: 7500,  emptyWeight: 3500 },
        { id: 'drive',    position: 5600, type: 'drive',    maxWeight: 11500, emptyWeight: 2800 },
        { id: 'tag',      position: 6950, type: 'tag',      maxWeight: 11500, emptyWeight: 1500 },
      ],
      axleGroups: [
        {
          id: 'tandem_rear',
          type: 'tandem',
          axleIds: ['drive', 'tag'],
          maxWeight: 19000,
          autoDetected: false,
        },
      ],
    },
    constraints: {
      maxWeight: 26000,
      maxStackHeight: 2600,
      maxFloorPressure: 10000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 7800,
  },

  // ── Semi-remorque 2 essieux remorque ─────────────────────────────────────────
  semi_trailer_2_axles: {
    id: 'semi_trailer_2_axles',
    label: 'Semi-remorque 2 essieux — masse brute indicative 34 t',
    description: 'Semi-remorque 2 essieux, tandem ; masse brute indicative du véhicule seul, à confirmer sur plaque.',
    category: 'semi',
    vehicleType: 'SEMI_TRAILER',
    dimensions: { length: 13600, width: 2550, height: 2800 },
    capacity: { weight: 34000 },
    axleConfiguration: {
      physicsType: 'tractor_semitrailer',
      kingPinPosition: 1500,
      kingPinHeight: 1150,
      fifthWheelMaxLoad: 12000,
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'trailer_1', position: 9200,  type: 'trailer', maxWeight: 9500, emptyWeight: 2200 },
        { id: 'trailer_2', position: 10600, type: 'trailer', maxWeight: 9500, emptyWeight: 2200 },
      ],
      axleGroups: [
        {
          id: 'tandem_trailer',
          type: 'tandem',
          axleIds: ['trailer_1', 'trailer_2'],
          maxWeight: 19000,
          autoDetected: false,
        },
      ],
    },
    fifthWheel: {
      position: 1500,
      type: 'standard',
      maxLoad: 12000,
    },
    constraints: {
      maxWeight: 34000,
      maxStackHeight: 2700,
      maxFloorPressure: 10000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 4400,
  },

  // ── Semi-remorque 3 essieux remorque ─────────────────────────────────────────
  semi_trailer_3_axles: {
    id: 'semi_trailer_3_axles',
    label: 'Semi-remorque 3 essieux — masse brute indicative 44 t',
    description: 'Semi-remorque 3 essieux, tridem ; masse brute indicative du véhicule seul, à confirmer sur plaque.',
    category: 'semi',
    vehicleType: 'SEMI_TRAILER',
    dimensions: { length: 13600, width: 2550, height: 3000 },
    capacity: { weight: 44000 },
    axleConfiguration: {
      physicsType: 'tractor_semitrailer',
      kingPinPosition: 1500,
      kingPinHeight: 1150,
      fifthWheelMaxLoad: 12000,
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'trailer_1', position: 9800,  type: 'trailer', maxWeight: 8000, emptyWeight: 2000 },
        { id: 'trailer_2', position: 11100, type: 'trailer', maxWeight: 8000, emptyWeight: 2000 },
        { id: 'trailer_3', position: 12400, type: 'trailer', maxWeight: 8000, emptyWeight: 2000 },
      ],
      axleGroups: [
        {
          id: 'tridem_rear',
          type: 'tridem',
          axleIds: ['trailer_1', 'trailer_2', 'trailer_3'],
          maxWeight: 24000,
          autoDetected: false,
        },
      ],
    },
    fifthWheel: {
      position: 1500,
      type: 'standard',
      maxLoad: 12000,
    },
    constraints: {
      maxWeight: 44000,
      maxStackHeight: 2800,
      maxFloorPressure: 12000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 6000,
  },

  // ── Remorque 2 essieux ────────────────────────────────────────────────────────
  trailer_2_axles: {
    id: 'trailer_2_axles',
    label: 'Remorque 2 essieux',
    description: 'Remorque autonome 2 essieux, tandem',
    category: 'remorque',
    vehicleType: 'TRAILER',
    dimensions: { length: 13600, width: 2550, height: 2800 },
    capacity: { weight: 24000 },
    axleConfiguration: {
      physicsType: 'trailer',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'axle_1', position: 6200,  type: 'trailer', maxWeight: 12000, emptyWeight: 2000 },
        { id: 'axle_2', position: 7700, type: 'trailer', maxWeight: 12000, emptyWeight: 2000 },
      ],
      axleGroups: [
        {
          id: 'tandem',
          type: 'tandem',
          axleIds: ['axle_1', 'axle_2'],
          maxWeight: 19000,
          autoDetected: false,
        },
      ],
    },
    constraints: {
      maxWeight: 24000,
      maxStackHeight: 2700,
      maxFloorPressure: 10000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 4000,
  },

  // ── Remorque 3 essieux ────────────────────────────────────────────────────────
  trailer_3_axles: {
    id: 'trailer_3_axles',
    label: 'Remorque 3 essieux',
    description: 'Remorque autonome 3 essieux, tridem',
    category: 'remorque',
    vehicleType: 'TRAILER',
    dimensions: { length: 13600, width: 2550, height: 2900 },
    capacity: { weight: 28000 },
    axleConfiguration: {
      physicsType: 'trailer',
      trackWidth: 2050,
      region: 'EU',
      enforceAxleLimits: true,
      axles: [
        { id: 'axle_1', position: 5700,  type: 'trailer', maxWeight: 9333, emptyWeight: 1800 },
        { id: 'axle_2', position: 7000,  type: 'trailer', maxWeight: 9333, emptyWeight: 1800 },
        { id: 'axle_3', position: 8300, type: 'trailer', maxWeight: 9333, emptyWeight: 1800 },
      ],
      axleGroups: [
        {
          id: 'tridem',
          type: 'tridem',
          axleIds: ['axle_1', 'axle_2', 'axle_3'],
          maxWeight: 24000,
          autoDetected: false,
        },
      ],
    },
    constraints: {
      maxWeight: 28000,
      maxStackHeight: 2700,
      maxFloorPressure: 10000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 5400,
  },

  // ── Conteneur ISO 20ft ────────────────────────────────────────────────────────
  container_20ft: {
    id: 'container_20ft',
    label: 'Conteneur 20 pieds',
    description: 'Conteneur maritime standard 20ft (6.1m) - sans essieux',
    category: 'conteneur',
    vehicleType: 'CONTAINER',
    dimensions: { length: 6058, width: 2438, height: 2591 },
    capacity: { weight: 30480 },
    axleConfiguration: {
      physicsType: 'container',
      trackWidth: 2438,
      region: 'EU',
      enforceAxleLimits: false,
      axles: [],
    },
    constraints: {
      maxWeight: 30480,
      maxStackHeight: 2590,
      maxFloorPressure: 15000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 2300,
  },

  // ── Conteneur ISO 40ft ────────────────────────────────────────────────────────
  container_40ft: {
    id: 'container_40ft',
    label: 'Conteneur 40 pieds',
    description: 'Conteneur maritime standard 40ft (12.2m) - sans essieux',
    category: 'conteneur',
    vehicleType: 'CONTAINER',
    dimensions: { length: 12192, width: 2438, height: 2591 },
    capacity: { weight: 30480 },
    axleConfiguration: {
      physicsType: 'container',
      trackWidth: 2438,
      region: 'EU',
      enforceAxleLimits: false,
      axles: [],
    },
    constraints: {
      maxWeight: 30480,
      maxStackHeight: 2590,
      maxFloorPressure: 15000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 3800,
  },

  // ── Conteneur ISO 40ft High Cube ────────────────────────────────────────────────
  container_40ft_high_cube: {
    id: 'container_40ft_high_cube',
    label: 'Conteneur 40 pieds High Cube',
    description: 'Conteneur maritime 40ft High Cube (12.2m x 2.9m) - sans essieux',
    category: 'conteneur',
    vehicleType: 'CONTAINER',
    dimensions: { length: 12192, width: 2438, height: 2896 },
    capacity: { weight: 30480 },
    axleConfiguration: {
      physicsType: 'container',
      trackWidth: 2438,
      region: 'EU',
      enforceAxleLimits: false,
      axles: [],
    },
    constraints: {
      maxWeight: 30480,
      maxStackHeight: 2890,
      maxFloorPressure: 15000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 4000,
  },

  // ── Conteneur ISO 45ft High Cube ────────────────────────────────────────────────
  container_45ft_high_cube: {
    id: 'container_45ft_high_cube',
    label: 'Conteneur 45 pieds High Cube',
    description: 'Conteneur maritime 45ft High Cube (13.7m x 2.9m) - sans essieux',
    category: 'conteneur',
    vehicleType: 'CONTAINER',
    dimensions: { length: 13716, width: 2438, height: 2896 },
    capacity: { weight: 30480 },
    axleConfiguration: {
      physicsType: 'container',
      trackWidth: 2438,
      region: 'EU',
      enforceAxleLimits: false,
      axles: [],
    },
    constraints: {
      maxWeight: 30480,
      maxStackHeight: 2890,
      maxFloorPressure: 15000,
      pressureCellTargetSize: 250,
    },
    emptyWeight: 4800,
  },
}

/** One geometric source of truth; chassis dimensions never describe a tractor's cargo volume. */
export function presetInteriorVolume(preset: Pick<VehicleEquipmentPreset, 'category' | 'dimensions'>): number | null {
  if (preset.category === 'tracteur') return null
  return vehicleInteriorVolume(preset.dimensions?.length, preset.dimensions?.width, preset.dimensions?.height)
}

export const VEHICLE_EQUIPMENT_PRESETS: Record<string, VehicleEquipmentPreset> = Object.fromEntries(
  Object.entries(PRESET_DEFINITIONS).map(([id, preset]) => [id, {
    ...preset,
    capacity: { ...preset.capacity, volume: presetInteriorVolume(preset) ?? undefined },
  }])
)

export interface ResolvedPresetMass {
  individualGrossWeight: number | null
  combinationGrossWeight: number | null
  payloadWeight: number | null
  source: 'indicative_catalog'
  version: 'vehicle_preset_mass.v1'
}

/**
 * Converts the historical preset weight into the explicit v1 mass contract.
 * Existing values are catalogue examples, never proof of a vehicle's plate.
 */
export function resolvePresetMass(preset: VehicleEquipmentPreset): ResolvedPresetMass {
  const gross = preset.mass?.individualGrossWeight
    ?? preset.constraints?.maxWeight
    ?? preset.capacity?.weight
    ?? null
  const tare = preset.emptyWeight ?? null
  const isTractor = preset.category === 'tracteur'
  const payloadWeight = !isTractor && gross != null && tare != null && gross >= tare
    ? gross - tare
    : null
  return {
    individualGrossWeight: gross,
    combinationGrossWeight: preset.mass?.combinationGrossWeight ?? null,
    payloadWeight,
    source: 'indicative_catalog',
    version: 'vehicle_preset_mass.v1'
  }
}

function withMassContract(preset: VehicleEquipmentPreset): VehicleEquipmentPreset {
  const mass = resolvePresetMass(preset)
  return {
    ...preset,
    mass: {
      version: mass.version,
      source: mass.source,
      ...(mass.individualGrossWeight != null ? { individualGrossWeight: mass.individualGrossWeight } : {}),
      ...(mass.combinationGrossWeight != null ? { combinationGrossWeight: mass.combinationGrossWeight } : {})
    },
    capacity: {
      ...preset.capacity,
      ...(mass.payloadWeight != null ? { weight: mass.payloadWeight } : {})
    },
    constraints: preset.constraints ? {
      ...preset.constraints,
      ...(mass.payloadWeight != null ? { maxWeight: mass.payloadWeight } : {})
    } : preset.constraints
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Calcule le centre de gravité cible selon la même logique que TargetCGResolver backend
 * @param preset - Le preset véhicule
 * @returns {{ x: number, y: number, source: string }}
 */
export function calculateTargetCG(preset: VehicleEquipmentPreset): { x: number; y: number; source: string } {
  const physicsType = preset.axleConfiguration.physicsType;
  const kingPinPosition = preset.axleConfiguration.physicsType === 'tractor_semitrailer'
    ? preset.axleConfiguration.kingPinPosition
    : null;
  const axles = preset.axleConfiguration.axles || [];
  const loadSpaceLength = preset.dimensions?.length || 12000;

  // Semi-remorque : CG cible entre king pin et groupe essieux remorque (inchangé)
  if (physicsType === 'tractor_semitrailer' && kingPinPosition != null && axles.length > 0) {
    const axlePositions = axles.map(a => a.position).filter(p => p != null);
    const rearGroupCenter = axlePositions.reduce((sum, p) => sum + p, 0) / axlePositions.length;
    const targetXmm = (kingPinPosition + rearGroupCenter) / 2;
    const targetX = Math.min(1, Math.max(0, targetXmm / loadSpaceLength));

    return {
      x: targetX,
      y: 0.5,
      source: 'semitrailer_physics',
    };
  }

  // Conteneur : centre géométrique (inchangé)
  if (physicsType === 'container') {
    return {
      x: 0.5,
      y: 0.5,
      source: 'container_center',
    };
  }

  // Van / Porteur / Remorque : calcul basé sur les essieux
  // Déterminer le ratio selon le type de véhicule
  let ratio = 0.5; // Par défaut
  let source = 'default_center';

  if (physicsType === 'van') {
    ratio = 0.48;
    source = 'van_axle_based';
  } else if (physicsType === 'rigid_truck') {
    // Porteur / Tracteur : ratio selon le nombre d'essieux
    // Note: Les tracteurs (4x2, 6x2) utilisent aussi 'rigid_truck'
    if (axles.length === 2) {
      ratio = 0.46;
      source = 'rigid_truck_2_axles';
    } else if (axles.length === 3) {
      ratio = 0.47;
      source = 'rigid_truck_3_axles';
    } else {
      ratio = 0.46; // Défaut pour autres configurations
      source = 'rigid_truck_default';
    }
  } else if (physicsType === 'trailer') {
    // Remorque : même logique que porteur selon nombre d'essieux
    if (axles.length === 2) {
      ratio = 0.46;
      source = 'trailer_2_axles';
    } else if (axles.length >= 3) {
      ratio = 0.47;
      source = 'trailer_3_axles';
    } else {
      ratio = 0.46;
      source = 'trailer_default';
    }
  }

  // Calculer targetCG à partir des positions d'essieux
  if (axles.length >= 2) {
    // Trier les essieux par position
    const sortedAxles = [...axles].sort((a, b) => (a.position || 0) - (b.position || 0));
    const frontAxle = sortedAxles[0];
    const rearAxle = sortedAxles[sortedAxles.length - 1];

    if (frontAxle?.position != null && rearAxle?.position != null) {
      const front = frontAxle.position;
      const rear = rearAxle.position;
      const wheelbase = rear - front;
      const targetXmm = front + wheelbase * ratio;
      const targetX = Math.min(1, Math.max(0, targetXmm / loadSpaceLength));

      return {
        x: targetX,
        y: 0.5,
        source,
      };
    }
  }

  // Fallback : centre géométrique si pas assez d'essieux
  return {
    x: 0.5,
    y: 0.5,
    source: 'fallback_center',
  };
}

/** Retourne la liste des presets filtrée par catégorie avec targetCG calculé */
export function getPresetsByCategory(category: VehicleEquipmentPreset['category']): VehicleEquipmentPreset[] {
  return Object.values(VEHICLE_EQUIPMENT_PRESETS)
    .filter(p => p.category === category)
    .map(withMassContract)
    .map(preset => ({
      ...preset,
      targetCG: calculateTargetCG(preset)
    }))
}

/** Retourne tous les presets sous forme de liste triée avec targetCG calculé */
export function getAllPresets(): VehicleEquipmentPreset[] {
  const order: VehicleEquipmentPreset['category'][] = ['van', 'porteur', 'tracteur', 'semi', 'remorque', 'conteneur']
  return Object.values(VEHICLE_EQUIPMENT_PRESETS)
    .map(withMassContract)
    .map(preset => ({
      ...preset,
      targetCG: calculateTargetCG(preset)
    }))
    .sort((a, b) =>
      order.indexOf(a.category) - order.indexOf(b.category)
    )
}

/** Labels des catégories pour l'UI */
export const PRESET_CATEGORY_LABELS: Record<VehicleEquipmentPreset['category'], string> = {
  van: 'Fourgonnettes',
  porteur: 'Porteurs rigides',
  tracteur: 'Tracteurs',
  semi: 'Semi-remorques',
  remorque: 'Remorques',
  conteneur: 'Conteneurs',
}

/** Icônes de catégorie (Heroicons name) */
export const PRESET_CATEGORY_ICONS: Record<VehicleEquipmentPreset['category'], string> = {
  van: '🚐',
  porteur: '🚛',
  tracteur: '🚚',
  semi: '🚜',
  remorque: '🔗',
  conteneur: '📦',
}
